/**
 * ExtraContentViewer — "VER MÁS" button + modal that shows a creator's
 * extra photos + video to authenticated visitors.
 *
 * Why a separate component (not inline in TipPage)
 * ────────────────────────────────────────────────
 * The button only renders if there's actual extra content AND the visitor
 * is authenticated. We do a HEAD-style fetch on mount so we don't show a
 * teaser that leads to an empty modal.
 *
 * Spec adherence
 * ──────────────
 * - Hidden for anonymous visitors (the underlying API is auth-gated anyway).
 * - Video clearly labeled "15 s máx".
 * - Renders nothing when the creator hasn't uploaded any extras.
 * - Visual style: pill button with a subtle gradient — distinguishable from
 *   the existing tip/like/follow CTAs without competing with them.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
export default function ExtraContentViewer({ creatorIdOrSlug, gender }) {
  const { user } = useAuth();
  const [extras, setExtras] = useState(null);   // null = not loaded, [] = empty
  const [open, setOpen]     = useState(false);
  // Internal lightbox index: number | null. The lightbox renders the image
  // inline (no anchor / target=_blank), so the S3 URL never leaks into the
  // browser's address bar.
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const loaded = extras !== null;
  const hasContent = loaded && (extras.extra_photos?.length > 0 || extras.extra_video);

  const load = useCallback(async () => {
    if (!user || !creatorIdOrSlug) { setExtras({ extra_photos: [], extra_video: null }); return; }
    try {
      const { data } = await axios.get(
        `${API}/creators/${encodeURIComponent(creatorIdOrSlug)}/extras`,
        { withCredentials: true },
      );
      setExtras(data);
    } catch {
      setExtras({ extra_photos: [], extra_video: null });
    }
  }, [user, creatorIdOrSlug]);

  useEffect(() => { load(); }, [load]);

  if (!hasContent) return null;
  const photos = extras.extra_photos || [];
  const video = extras.extra_video;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="extras-open-btn"
        className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-neon-pink to-fuchsia-600 hover:from-neon-pink/90 hover:to-fuchsia-500 text-white font-heading font-bold text-xs uppercase tracking-wider shadow-lg shadow-fuchsia-900/30 hover:shadow-fuchsia-700/50 transition-all active:scale-95"
      >
        <Sparkles className="w-4 h-4" />
        Ver más
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setOpen(false)}
          data-testid="extras-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-3xl p-5 sm:p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-full bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              data-testid="extras-modal-close"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>

            <div>
              <h2 className="text-base font-heading font-bold uppercase tracking-wide text-white">
                {gender === "hombre" ? "Algo más sobre él" : "Algo más sobre ella"}
              </h2>
              <p className="text-xs text-zinc-500 font-body mt-0.5">
                {gender === "hombre"
                  ? "Contenido extra subido por el propio modelo."
                  : "Contenido extra subido por la propia modelo."}
              </p>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="extras-photo-grid">
                {photos.map((p, i) => (
                  <button
                    key={p.url}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    className="aspect-square rounded-xl overflow-hidden border border-zinc-800 hover:border-neon-pink/50 transition-colors focus:outline-none focus:border-neon-pink"
                    data-testid={`extras-photo-${i}`}
                    aria-label={`Abrir foto ${i + 1}`}
                  >
                    <img
                      src={p.url}
                      alt={`Foto ${i + 1}`}
                      className="w-full h-full object-cover pointer-events-none select-none"
                      loading="lazy"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </button>
                ))}
              </div>
            )}

            {video?.url && (
              <div data-testid="extras-video-wrapper" className="space-y-1.5">
                <video
                  src={video.url}
                  controls
                  playsInline
                  controlsList="nodownload"
                  className="w-full rounded-2xl border border-zinc-800 bg-black max-h-[60vh]"
                />
                <p className="text-[10px] text-zinc-500 font-body text-center">
                  Vídeo corto · máx. 15 segundos
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {open && lightboxIdx !== null && photos[lightboxIdx] && (
        <Lightbox
          photos={photos}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onChange={setLightboxIdx}
        />
      )}
    </>
  );
}

/**
 * Internal full-screen image viewer.
 *
 * Why a custom component instead of `<a target="_blank">`
 * ──────────────────────────────────────────────────────
 * The browser's native image viewer shows the absolute URL in the address
 * bar, exposing the S3 hosting domain. Users were able to copy that URL
 * and share / hotlink the image. This viewer keeps the user inside the SPA:
 * no URL change, no right-click menu (best-effort), no drag-to-save.
 *
 * UX
 * ──
 * - Keyboard: ← / → to navigate, ESC to close.
 * - Touch: horizontal swipe to switch images (threshold 50 px).
 * - Click on the dark backdrop closes; clicks on the image or the side
 *   buttons don't propagate.
 * - Counter ("3 / 7") so users know there's more to swipe to.
 */
function Lightbox({ photos, index, onClose, onChange }) {
  const total = photos.length;
  const go = useCallback((delta) => {
    onChange((index + delta + total) % total);
  }, [index, total, onChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);

  // Touch swipe handlers (mobile)
  const [touchStartX, setTouchStartX] = useState(null);
  const onTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1);
    setTouchStartX(null);
  };

  const current = photos[index];

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="extras-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 p-2 rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors z-10"
        data-testid="extras-lightbox-close"
        aria-label="Cerrar"
      >
        <X className="w-5 h-5" />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-900/70 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors z-10"
            data-testid="extras-lightbox-prev"
            aria-label="Foto anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-900/70 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors z-10"
            data-testid="extras-lightbox-next"
            aria-label="Foto siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <img
        src={current.url}
        alt={`Foto ${index + 1} de ${total}`}
        className="max-w-[95vw] max-h-[90vh] object-contain select-none"
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="extras-lightbox-image"
      />

      {total > 1 && (
        <div
          className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-zinc-900/80 text-zinc-300 text-xs font-body tabular-nums"
          data-testid="extras-lightbox-counter"
        >
          {index + 1} / {total}
        </div>
      )}
    </div>
  );
}
